import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';

interface ActiveCustomerRow {
  accountId: string;
  accountName: string;
  industry: string | null;
  wonOpportunities: number;
  totalWonValue: number;
  lastCloseDate: string | null;
}

interface RenewalCandidate {
  accountId: string;
  accountName: string;
  opportunityId: string;
  opportunityName: string;
  revenueType: string;
  renewalDate: string;
  daysUntilRenewal: number;
}

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value.toFixed(0)}`;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '\u2014';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

function renewalColor(days: number): string {
  if (days < 30) return 'text-red-600';
  if (days <= 60) return 'text-amber-600';
  return 'text-emerald-600';
}

export function CustomersPage() {
  const [customers, setCustomers] = useState<ActiveCustomerRow[]>([]);
  const [renewals, setRenewals] = useState<RenewalCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [custRes, renewalRes] = await Promise.all([
        apiClient.get<ActiveCustomerRow[]>('/analytics/customers'),
        apiClient.get<RenewalCandidate[]>('/analytics/renewal-candidates', {
          params: { withinDays: 90 },
        }),
      ]);
      setCustomers(custRes.data);
      setRenewals(renewalRes.data);
    } catch {
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Customer Success</h1>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <>
          {/* Active Customers */}
          <section className="space-y-3">
            <h2 className="text-lg font-medium">Active Customers</h2>
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Account Name</th>
                    <th className="px-4 py-3 text-right">Won Value</th>
                    <th className="px-4 py-3 text-right">Opp Count</th>
                    <th className="px-4 py-3 text-right">Last Won Date</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((row) => (
                    <tr key={row.accountId} className="border-t">
                      <td className="px-4 py-3">
                        <Link
                          to={`/accounts/${row.accountId}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {row.accountName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(row.totalWonValue)}
                      </td>
                      <td className="px-4 py-3 text-right">{row.wonOpportunities}</td>
                      <td className="px-4 py-3 text-right">
                        {formatDate(row.lastCloseDate)}
                      </td>
                    </tr>
                  ))}
                  {customers.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No active customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Renewal Candidates */}
          <section className="space-y-3">
            <h2 className="text-lg font-medium">Renewal Candidates</h2>
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Account</th>
                    <th className="px-4 py-3">Line Item</th>
                    <th className="px-4 py-3">Revenue Type</th>
                    <th className="px-4 py-3 text-right">Renewal Date</th>
                    <th className="px-4 py-3 text-right">Days Until Renewal</th>
                  </tr>
                </thead>
                <tbody>
                  {renewals.map((row) => (
                    <tr key={`${row.opportunityId}-${row.revenueType}`} className="border-t">
                      <td className="px-4 py-3">
                        <Link
                          to={`/accounts/${row.accountId}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {row.accountName}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{row.opportunityName}</td>
                      <td className="px-4 py-3">{row.revenueType}</td>
                      <td className="px-4 py-3 text-right">{formatDate(row.renewalDate)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${renewalColor(row.daysUntilRenewal)}`}>
                        {row.daysUntilRenewal} days
                      </td>
                    </tr>
                  ))}
                  {renewals.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No renewal candidates in the next 90 days
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
